/* =============================================================================
   validate.js - pure functions. No DOM, no state, no side effects.
   Requires PlateConfig (config.js) and must be loaded after it.
   Uses PlateDistricts (data/districts.js) when present, for the soft city
   code check only.
   Error messages are user-facing and therefore German.
   ========================================================================== */
var PlateValidate = (function (cfg, districts) {
  'use strict';

  var L = cfg.LIMITS;

  /* --- Normalization ------------------------------------------------------- */

  // Letters of the Erkennungsnummer: uppercase, A-Z only, truncate. Umlauts do
  // not occur in this part of the plate.
  function cleanLetters(value, max) {
    var s = String(value == null ? '' : value).toUpperCase().replace(/[^A-Z]/g, '');
    return max ? s.slice(0, max) : s;
  }

  // The Unterscheidungszeichen does allow umlauts - LÖ, WÜ, MÜ, TÖL, SÄK are
  // real codes, so A-Z alone would make those plates impossible to order.
  function cleanCityCode(value, max) {
    var s = String(value == null ? '' : value).toUpperCase().replace(/[^A-ZÄÖÜ]/g, '');
    return max ? s.slice(0, max) : s;
  }

  // Digits: drop everything but 0-9, truncate.
  function cleanDigits(value, max) {
    var s = String(value == null ? '' : value).replace(/[^0-9]/g, '');
    return max ? s.slice(0, max) : s;
  }

  // Raw form input -> clean state. Used on every keystroke so the input fields
  // and the preview always see exactly the same values.
  // Grossbuchstaben, Ziffern und einfache Leerzeichen, mehr braucht ein
  // Parkplatzschild nicht. Die Laenge begrenzt die Groesse selbst (maxText).
  function cleanParkingText(value) {
    return String(value == null ? '' : value)
      .toUpperCase()
      .replace(/[^A-ZÄÖÜ0-9 -]/g, '')
      .replace(/\s+/g, ' ')
      .slice(0, 16);
  }

  function normalize(raw) {
    var r = raw || {};
    var quantity = parseInt(r.quantity, 10);
    return {
      cityCode:   cleanCityCode(r.cityCode, L.cityCode.max),
      letters:    cleanLetters(r.letters, L.letters.max),
      digits:     cleanDigits(r.digits, L.digits.max),
      sizeId:     r.sizeId || cfg.SIZES[0].id,
      type:       r.type || cfg.TYPES[0].id,
      // Freie Zeile des Parkplatzschilds. Sie steht neben den Kennzeichenfeldern
      // statt an ihrer Stelle, damit keine der bestehenden Regeln sie sieht -
      // ein Parkplatzschild ist kein Kennzeichen und wird auch nicht so geprueft.
      parkingText: cleanParkingText(r.parkingText),
      seasonFrom: r.seasonFrom || '',
      seasonTo:   r.seasonTo || '',
      quantity:   (quantity === 2) ? 2 : 1
    };
  }

  /* --- Single rules -------------------------------------------------------- */

  function err(field, message) { return { field: field, message: message }; }

  /* --- District lookup ------------------------------------------------------
     districts is optional: if data/districts.js fails to load, the lookup
     simply returns null and the configurator keeps working without the
     confirmation line. It must never be able to block an order.              */
  function lookupDistrict(cityCode) {
    if (!districts || !districts.DISTRICTS) { return null; }
    var code = cleanCityCode(cityCode, L.cityCode.max);
    var entry = districts.DISTRICTS[code];
    if (!entry) { return null; }
    var state = districts.STATES ? districts.STATES[entry.state] : null;
    return {
      code: code,
      district: entry.district,
      state: state || { id: entry.state, name: entry.state, flagColors: [] }
    };
  }

  function isForbiddenCombo(letters) {
    var list = cfg.FORBIDDEN_COMBOS;
    for (var i = 0; i < list.length; i++) {
      if (list[i] === letters) { return true; }
    }
    return false;
  }

  function totalChars(state) {
    return state.cityCode.length + state.letters.length + state.digits.length;
  }

  function isMonth(value) {
    return /^(0[1-9]|1[0-2])$/.test(value);
  }

  // Inclusive season length in months, wrap-around aware:
  // 03 -> 10 = 8, 11 -> 02 = 4, 03 -> 03 = 1, 01 -> 12 = 12.
  // Returns null if either month is invalid.
  function seasonLength(from, to) {
    if (!isMonth(from) || !isMonth(to)) { return null; }
    var f = parseInt(from, 10);
    var t = parseInt(to, 10);
    return ((t - f + 12) % 12) + 1;
  }

  /* --- Main validation ------------------------------------------------------
     Accepts raw or normalized input (normalize() is applied internally).
     Returns { ok: Boolean, errors: [...], warnings: [...] }, both lists of
     { field, message }.

     Errors block the order. Warnings never do: ok only ever looks at errors.
     Error fields: 'cityCode' | 'letters' | 'digits' | 'season' | 'general'.
     'general' is the slot above the submit button and also catches select
     values that no legitimate form interaction can produce - never fail
     silently on a tampered or broken form.                                    */
  function validate(rawOrState) {
    var s = normalize(rawOrState);
    var errors = [];
    var warnings = [];

    // 0. Parkplatzschild: kein Kennzeichen, also auch keine Kennzeichen-Regeln.
    //    Geprueft wird nur, ob ueberhaupt etwas dasteht und ob es auf das
    //    Schild passt. Menge und Groesse gelten wie ueberall.
    var parkSize = cfg.getSize(s.sizeId);
    if (parkSize && parkSize.parking) {
      var maxText = parkSize.maxText || 9;
      if (!s.parkingText.length) {
        errors.push(err('parkingText', 'Bitte Text für das Schild eingeben.'));
      } else if (s.parkingText.length > maxText) {
        errors.push(err('parkingText', 'Maximal ' + maxText + ' Zeichen.'));
      }
      var parkQuantity = (rawOrState || {}).quantity;
      if (parkQuantity !== undefined && parkQuantity !== null && parkQuantity !== '' &&
          parseInt(parkQuantity, 10) !== 1 && parseInt(parkQuantity, 10) !== 2) {
        errors.push(err('general', 'Ungültige Menge.'));
      }
      return { ok: errors.length === 0, errors: errors, warnings: warnings };
    }

    // 1. City code (Unterscheidungszeichen)
    if (s.cityCode.length < L.cityCode.min) {
      errors.push(err('cityCode', 'Bitte Unterscheidungszeichen eingeben (1–3 Buchstaben).'));
    } else if (s.cityCode.length > L.cityCode.max) {
      errors.push(err('cityCode', 'Maximal ' + L.cityCode.max + ' Buchstaben.'));
    } else if (districts && districts.DISTRICTS && !lookupDistrict(s.cityCode)) {
      // Soft check only. The list ages as codes are retired and reissued, and
      // a stale list must never cost an order.
      warnings.push(err('cityCode', 'Das Unterscheidungszeichen „' + s.cityCode +
        '“ kennen wir nicht. Bitte prüfe die Eingabe - bestellen kannst du trotzdem.'));
    }

    // 2. Erkennungsnummer - letter part
    if (s.letters.length < L.letters.min) {
      errors.push(err('letters', 'Bitte 1–2 Buchstaben eingeben.'));
    } else if (s.letters.length > L.letters.max) {
      errors.push(err('letters', 'Maximal ' + L.letters.max + ' Buchstaben.'));
    } else if (isForbiddenCombo(s.letters)) {
      errors.push(err('letters', 'Die Buchstabenkombination „' + s.letters + '“ ist nicht zulässig.'));
    }

    // 3. Erkennungsnummer - digit part
    if (s.digits.length < L.digits.min) {
      errors.push(err('digits', 'Bitte mindestens 1 Ziffer eingeben.'));
    } else if (s.digits.length > L.digits.max) {
      errors.push(err('digits', 'Maximal ' + L.digits.max + ' Ziffern.'));
    } else if (s.digits.charAt(0) === '0') {
      errors.push(err('digits', 'Die Ziffernfolge darf nicht mit 0 beginnen.'));
    }

    // 4. Total length -> rendered in the general error slot above the button
    if (totalChars(s) > L.totalChars) {
      errors.push(err('general', 'Maximal ' + L.totalChars + ' Zeichen insgesamt (aktuell ' + totalChars(s) + ').'));
    }

    // 5. Size
    if (!cfg.getSize(s.sizeId)) {
      errors.push(err('general', 'Ungültige Größe.'));
    }

    // 6. Type and season months
    var type = cfg.getType(s.type);
    if (!type) {
      errors.push(err('general', 'Ungültiger Kennzeichen-Typ.'));
    } else if (type.isSeason) {
      if (!isMonth(s.seasonFrom) || !isMonth(s.seasonTo)) {
        errors.push(err('season', 'Bitte Saison-Beginn und Saison-Ende wählen.'));
      } else {
        // Wrap-around seasons such as 11 -> 02 are legal; length must be 2-11 months.
        var months = seasonLength(s.seasonFrom, s.seasonTo);
        if (months < L.season.minMonths) {
          errors.push(err('season', 'Die Saison muss mindestens ' + L.season.minMonths + ' Monate dauern.'));
        } else if (months > L.season.maxMonths) {
          errors.push(err('season', 'Die Saison darf höchstens ' + L.season.maxMonths + ' Monate dauern.'));
        }
      }
    }

    // 7. Quantity. normalize() coerces to 1 or 2, so the raw value is checked
    // here - otherwise a tampered form would silently order one plate.
    var rawQuantity = (rawOrState || {}).quantity;
    if (rawQuantity !== undefined && rawQuantity !== null && rawQuantity !== '' &&
        parseInt(rawQuantity, 10) !== 1 && parseInt(rawQuantity, 10) !== 2) {
      errors.push(err('general', 'Ungültige Menge.'));
    }

    // Warnings are deliberately not part of ok.
    return { ok: errors.length === 0, errors: errors, warnings: warnings };
  }

  /* --- UI helpers ---------------------------------------------------------- */

  function toMap(list) {
    var map = {};
    var items = list || [];
    for (var i = 0; i < items.length; i++) {
      (map[items[i].field] = map[items[i].field] || []).push(items[i].message);
    }
    return map;
  }

  // All messages for one field, e.g. messagesFor(result, 'digits').
  function messagesFor(result, field) {
    var out = [];
    var e = (result && result.errors) || [];
    for (var i = 0; i < e.length; i++) {
      if (e[i].field === field) { out.push(e[i].message); }
    }
    return out;
  }

  // { cityCode: ['...'], digits: ['...'] } - convenient for inline rendering.
  function errorMap(result) {
    return toMap(result && result.errors);
  }

  function warningMap(result) {
    return toMap(result && result.warnings);
  }

  /* --- Display strings ------------------------------------------------------
     "HD-AB 1234", with suffix "HD-AB 1234E" / "...H".
     Works for incomplete input as well (live preview).                        */
  function formatKennzeichen(rawOrState) {
    var s = normalize(rawOrState);
    // Auf einem Parkplatzschild steht die freie Zeile und sonst nichts.
    var size = cfg.getSize(s.sizeId);
    if (size && size.parking) { return s.parkingText; }
    var type = cfg.getType(s.type);
    var suffix = (type && type.suffix) ? type.suffix : '';
    var left = s.cityCode;
    var right = (s.letters + (s.digits ? ' ' + s.digits : '')).trim();
    if (!left) { return right ? right + suffix : ''; }
    if (!right) { return left; }
    return left + '-' + right + suffix;
  }

  // "03/10" or null
  function formatSeason(rawOrState) {
    var s = normalize(rawOrState);
    var type = cfg.getType(s.type);
    if (!type || !type.isSeason) { return null; }
    if (!isMonth(s.seasonFrom) || !isMonth(s.seasonTo)) { return null; }
    return s.seasonFrom + '/' + s.seasonTo;
  }

  return {
    cleanLetters: cleanLetters,
    cleanCityCode: cleanCityCode,
    cleanDigits: cleanDigits,
    normalize: normalize,
    validate: validate,
    lookupDistrict: lookupDistrict,
    messagesFor: messagesFor,
    errorMap: errorMap,
    warningMap: warningMap,
    formatKennzeichen: formatKennzeichen,
    formatSeason: formatSeason,
    totalChars: totalChars,
    seasonLength: seasonLength,
    isMonth: isMonth
  };
// data/districts.js is optional on purpose: if it does not load, the district
// confirmation and its warning disappear, and ordering keeps working.
})(PlateConfig, typeof PlateDistricts !== 'undefined' ? PlateDistricts : null);
